import { ReactNode, useMemo } from 'react';
import { Flex, FlexProps } from '@blockanalitica/ui';
import { NavLink as RouterNavLink, useParams } from 'react-router';
import styled, { css } from 'styled-components';

export type NavigationRoute = {
  title: string;
  linkTo: string;
  icon?: ReactNode;
  end?: boolean;
};

export interface NavigationProps extends FlexProps {
  routes: NavigationRoute[];
}

const StyledIcon = styled(Flex)`
  color: ${(props) => props.theme.colors.spark.gray30};
`;

const NavLink = styled(RouterNavLink)`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;

  ${(props) => css`
    gap: ${props.theme.space['2xs']};
    color: ${props.theme.colors.text};
    font-size: ${props.theme.sizes['1']};

    &:hover,
    &:hover ${StyledIcon} {
      color: ${props.theme.colors.linkHover};
    }

    &.active,
    &.active ${StyledIcon} {
      position: relative;
      color: ${props.theme.colors.primary};
    }
  `}
`;

export default function Navigation({ routes, ...rest }: NavigationProps) {
  const { network } = useParams();

  const networkRoutes = useMemo(
    () =>
      routes.map((route) => ({
        ...route,
        linkTo:
          route.linkTo === '/' ? route.linkTo : `/${network}${route.linkTo}`
      })),
    [network, routes]
  );

  return (
    <Flex alignItems="center" gap="s" {...rest}>
      {networkRoutes.map((route) => (
        <NavLink key={route.linkTo} to={route.linkTo} end={route.end}>
          <StyledIcon>{route.icon}</StyledIcon>
          {route.title}
        </NavLink>
      ))}
    </Flex>
  );
}
